<template>
    <oss-dialog
        v-model="dialog"
        @valid="valid"
        :title="stepTitle"
        :validTitle="validTitle"
        :validLoading="validLoading"
        :valid-disabled="!validProcess"
        :hideValid="!validDisplay"
        :previous-display="displayPrevious"
        @previous="previous()"
        prefixDataCy="FullBudgetProcess-oss-dialog"
        :min-width="'100vw'"
    >
        <v-card v-if="loading">
            <v-progress-linear height="10" indeterminate />
        </v-card>
        <v-card style="background-color: transparent" elevation="0" class="mx-0" v-else>
            <v-row no-gutters>
                <v-col cols="3">
                    <v-text-field-filled v-model="budgetName" density="compact" />
                </v-col>
                <v-col cols="2" class="pl-2">
                    <oss-date-picker-filled
                        v-model="date"
                        type="month"
                        format="MM/YYYY"
                        :text-field-readonly="false"
                        hide-details="auto"
                        @change-date="onDateChanged"
                    />
                </v-col>
                <v-col cols="2" class="pl-2" v-if="step !== 6">
                    <v-select-filled
                        v-model="selectedPartition"
                        :items="partitionTags"
                        item-value="id"
                        density="compact"
                        required
                        hide-details="auto"
                        prepend-inner-icon="$fa-gear"
                        @click:prepend-inner="openTagManager"
                        @update:model-value="initializeDefaultTag()"
                        :item-title="(item) => item.name"
                    />
                </v-col>
                <v-col class="pl-2" v-if="displayReinitializationButton">
                    <v-btn class="d-flex justify-end" rounded elevation="0" @click="reinitialize">{{
                        reinitializationText
                    }}</v-btn>
                </v-col>
            </v-row>

            <v-window v-model="step" touchless class="elevation-0" style="background: transparent">
                <v-window-item :value="1">
                    <v-card-text class="py-0">
                        <v-list subheader lines="two">
                            <v-list-item
                                class="oss-list-item mb-2 rounded-lg"
                                @click="onClassicMethodChosen"
                                data-cy="FullBudgetProcess-methode_clasique"
                            >
                                <template v-slot:append>
                                    <v-btn-next />
                                </template>
                                <v-list-item-title>Méthode classique</v-list-item-title
                                ><v-list-item-subtitle
                                    >Recommandée pour un démarrage ou une estimation simple de votre
                                    budget sur plusieurs années</v-list-item-subtitle
                                ></v-list-item
                            >
                            <v-list-item
                                class="oss-list-item mb-2 rounded-lg"
                                @click="onNM1MethodChosen"
                            >
                                <template v-slot:append>
                                    <v-btn-next />
                                </template>
                                <v-list-item-title
                                    >Méthode basée sur l'exercice N-1</v-list-item-title
                                ><v-list-item-subtitle
                                    >Recommandée pour avoir un budget précis sur 1
                                    an</v-list-item-subtitle
                                ></v-list-item
                            >
                            <!-- <v-list-item class="mb-2 oss-list-item rounded-lg" @click="onManualMethodChosen">
                                    <template>
                                        <v-list-item-title>Méthode manuel</v-list-item-title>
                                        <v-list-item-subtitle>Manuel</v-list-item-subtitle>
                                        Définisser pour chaque compte comptable et chaque mois, un montant
                                    </template>
                                    <template v-slot:append>
                                        <v-btn icon>
                                            <v-icon color="grey lighten-1">$chevron_right</v-icon>
                                        </v-btn>
                                    </template>
                                </v-list-item> -->
                        </v-list>
                    </v-card-text>
                </v-window-item>
                <v-window-item :value="2">
                    <v-card-text class="py-0" style="overflow-y: auto">
                        <v-alert type="error" v-if="!selectedAccountingPeriod">
                            Si aucune donnée n'apparaît, assurez-vous d'avoir bien sélectionné une
                            période comptable, puis recommencez.
                        </v-alert>
                        <BudgetFormAccountableAccount
                            :chargesGroupingMode="bffGenerationMode"
                            :formBfaa="formBfaa"
                            v-if="formBfaa"
                        />
                    </v-card-text>
                </v-window-item>
                <v-window-item :value="3">
                    <v-card-text class="py-0" style="overflow-y: auto">
                        <v-row>
                            <v-col cols="12">
                                <div class="body-1 px-2" style="text-align: justify">
                                    Vous pouvez maintenant choisir comment regrouper vos lignes de
                                    charges de l'année précédente.
                                </div>
                                <div class="body-1 px-2" style="text-align: justify">
                                    Vous pouvez les regrouper par compte comptable, ou bien par
                                    fournisseur.
                                </div>
                                <div class="body-1 px-2" style="text-align: justify">
                                    Cela signifie qu'une seule ligne par type de montant (charge,
                                    activité, personnel) et par entreprise ou compte comptable sera
                                    créée dans votre budget.
                                </div>
                                <!-- <div class="body-1 px-2" style="text-align: justify">
                                        Néanmoins, cette méthode de regroupement ne s'applique que pour vos charges. Vos ventes seront forcément regroupées par compte comptable.
                                    </div> -->
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-radio-group v-model="bffGenerationMode" column color="primary">
                                <v-radio
                                    label="Regroupement des lignes par entreprise"
                                    value="TPC"
                                />
                                <v-radio
                                    label="Regroupement des lignes par compte comptable"
                                    value="AA"
                                />
                            </v-radio-group>
                        </v-row>
                    </v-card-text>
                </v-window-item>
                <v-window-item :value="4">
                    <v-card-text class="py-0" style="overflow-y: auto">
                        <BudgetParameters :parameters="parameters" />
                    </v-card-text>
                </v-window-item>
                <v-window-item :value="5">
                    <v-card-text class="py-0">
                        <BudgetFormFull
                            :formBff="formBff"
                            :groupingMode="bffGenerationMode"
                            :date="date"
                            :parameters="parameters"
                            :availableSalesAccounts="availableSalesAccounts"
                            :partitionTags="partitionTags"
                            :selectedPartition="selectedPartition"
                            :initializationPackage="initializationPackage"
                            :headersPackage="headersPackage"
                            :selectedCompany="selectedCompany"
                            @validation-update="onValidationUpdate($event)"
                        />
                    </v-card-text>
                </v-window-item>
                <!-- <v-window-item :value="6">
                    <v-card-text class="py-0">
                        <BudgetManual
                            :name="budgetName"
                            :date="date"
                            :budgetId="budgetId"
                            @budget-id="budgetId = $event"
                        />
                    </v-card-text>
                </v-window-item> -->
            </v-window>
        </v-card>
    </oss-dialog>
    <oss-dialog
        v-model="displayTagManager"
        title="Gestionnaire d'étiquettes"
        @close="getCreatedPartitionTags"
    >
        <tag-manager> </tag-manager>
    </oss-dialog>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";
import { State2Way } from "@/decorators";

import moment from "moment-timezone";
import jsonpack from "jsonpack";
import { cloneDeep } from "lodash";

import YEARLY_6_AND_7_BALANCES from "../../graphql/budget/Yearly6And7Balances.gql";
import YEARLY_TO_MONTHLY_6_AND_7_BALANCES from "../../graphql/budget/YearlyToMonthly6And7Balances.gql";
import BUDGET_QUERY from "../../graphql/budget/Budget.gql";
import REVENUE_ACCOUNTABLE_ACCOUNTS_QUERY from "../../graphql/accountableAccount/RevenueAccountableAccounts.gql";
import PARTITION_TAG_QUERY from "../../graphql/tag/CompanyPartitionTags.gql";
import CREATE_UPDATE_BUDGET_FULL from "../../graphql/budget/CreateUpdateBudgetFull.gql";

import BudgetFormAccountableAccount from "./BudgetFormAccountableAccount.vue";
import BudgetFormFull from "./BudgetFormFull.vue";
import BudgetParameters from "./BudgetParameters.vue";
import TagManager from "../tag/TagManager.vue";
// import BudgetManual from "./BudgetManual.vue";

import {
    getOrderedMonthsFirstPeriod,
    getOrderedMonthsPeriods,
    buildActivityYearlyDetailsItems,
    buildChargeYearlyDetailsItems,
    buildStaffYearlyDetailsItems,
} from "../../services/budgetFormFullDetails";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop, Model, Emit } from "vue-facing-decorator";

@Component({
    components: {
        BudgetFormAccountableAccount,
        BudgetFormFull,
        BudgetParameters,
        TagManager,
        // BudgetManual,
    },
})
export class FullBudgetProcess extends Vue {
    @Model({ default: false }) value: boolean;
    @Prop({ default: "" }) budgetId: string;
    @Prop({ default: "" }) budgetIdToCopy: string;

    @State2Way("updateSelectedAccountingPeriod", "accountingPeriod.selectedAccountingPeriod")
    selectedAccountingPeriod!: any;

    // ATTRIBUTES

    step: number = 1; // step counter
    validLoading: boolean = false;
    // width: string = "95%"
    // height: string = "95%"

    validBff: boolean = true; // help enable process validation if last step is valid

    showInitialization: boolean = false; // show 2 NM1 steps if this method is chosen
    bffStartStep: number = 2;

    // keyRefresh: number = 0 // incremented when step numbers have to be refreshed, while a method is changed

    bffGenerationMode: string = "AA"; // pilot the line grouping criteria in the bff, id est by TPC or AA
    emitForm: boolean = false; // when true, launch an emit that allow this file to get the bfaa form in its latest version
    formBfaa: any = null; // form containing the information about which existing lines are reported in the new budget
    // accountingPeriod: any = null // details about the accounting period selected to perform the NM1 approach (start, end, id)
    datasets: Array<string> = ["charges", "sales", "staff"];
    reportedLines: any = null; // final output of the NM1 method, to be given to BudgetFormFull

    skipBudgetQuery: boolean = true;
    skipTagQuery: boolean = false;
    skip6And7Query: boolean = true;

    newBudgetId: string = ""; // store the newly created id after createUpdateBudgetFull in case of a creation
    budgetName: string = "Nouveau scénario";
    date: string = moment().format();
    selectedPartition: string = "";
    availableSalesAccounts: Array<any> = [];

    instantiated: boolean = false;

    displayTagManager: boolean = false;
    isTagManagerClosing: boolean = false;
    updateBffHeaders: boolean = false; // property to pass to bff to include or delete the tag column depending on the selection of a partition

    latestDate: string = ""; // transition value to allow computations in details

    // refreshHeadersOnPartitionChanged: number = 0

    loading: boolean = false;

    // INITIALIZED OBJECTS

    formBff: any = {
        fundings: {
            capitalContributions: [],
            currentAccountContributions: [],
            loans: [],
            investmentGrants: [],
        },
        activities: [],
        charges: {
            recurringCharges: [],
            uniqueCharges: [],
        },
        staff: [],
    };

    parameters: any = {
        periodsNumber: 1,
        firstPeriodMonthsNumber: 12,
        // extendedPrevisionalDuration: 5,
        paymentDelayClient: 1,
        paymentDelaySupplierPurchase: 1,
        paymentDelaySupplierCosts: 1,
        vatSell: 20.0,
        vatPurchase: 20.0,
        vatImmo: 20.0,
        vatCost: 20.0,
        vatOther: 20.0,
        wagesTax: 22.0,
        employerTax: 38.0,
        globalTax: 40.0,
        // monthOfWagesPayment: "Mois en cours",
        // percentageOfCalendarQuarterPayment: 0,
        // percentageOfMonthlyPayment: 100,
        // paidHolidayFund: {
        //     disabled: true,
        //     employerContributionRate: 20,
        //     holidaysSaisonnality: 0
        // },
        loanInsuranceMode: "CE",
    };

    blankInvestment = {
        isLineReported: false,
        amountNM1: null,
        detailsNM1: [],
        activeReporting: null,
        tag: null,
        title: null,
        nature: "Incorporel",
        monthGap: 0,
        // date: this.date,
        amount: 0,
        staggering: "Aucun",
        amortizationLaw: "Aucun",
        staggeringOffset: 0,
        staggeringDuration: 12,
        paymentNumber: 1,
        paymentSpacing: 1,
        vatRate: 20,
        vatCollectionTime: "Au décaissement",
    };

    blankCapitalContribution = {
        isLineReported: false,
        tag: null,
        title: null,
        monthGap: 0,
        // date: this.date,
        amount: 0,
    };

    blankCurrentAccountContribution = {
        isLineReported: false,
        tag: null,
        name: null,
        monthGap: 0,
        // date: this.date,
        amount: 0,
    };

    blankLoan = {
        isLineReported: false,
        tag: null,
        title: null,
        details: [],
        monthGap: 0,
        // date: this.date,
        amount: 0,
        rate: 0,
        duration: 12,
        periodicity: 12,
        insuranceRate: 0,
        monthlyAmount: 0,
        totalCost: 0,
    };

    blankInvestmentGrant = {
        isLineReported: false,
        tag: null,
        title: null,
        monthGap: 0,
        // date: this.date,
        amount: 0,
        amortizationLaw: "Linéaire",
        amortizationDuration: 12,
    };

    blankActivity = {
        isLineReported: false,
        amountNM1: null,
        detailsNM1: [],
        activeReporting: null,
        tag: null,
        title: null,
        // sector: "Services",
        salesAccount: null,
        details: [],
        years: [
            {
                title: "Montant N",
                amount: 0,
                evolutionPercentage: null,
            },
            {
                title: "Montant N+1",
                amount: 0,
                evolutionPercentage: 0,
            },
            {
                title: "Montant N+2",
                amount: 0,
                evolutionPercentage: 0,
            },
        ],
        marginRate: 100,
        vatRate: 20,
        paymentDelay: 0,
        vatCollectionTime: "Au décaissement",
    };

    blankCharge = {
        isLineReported: false,
        amountNM1: null,
        detailsNM1: [],
        activeReporting: null,
        tag: null,
        title: null,
        details: [],
        years: [
            {
                title: "Montant N",
                amount: 0,
                evolutionPercentage: null,
            },
            {
                title: "Montant N+1",
                amount: 0,
                evolutionPercentage: 0,
            },
            {
                title: "Montant N+2",
                amount: 0,
                evolutionPercentage: 0,
            },
        ],
        // fixedPercentage: 100,
        frequency: 12,
        paymentDelay: 0,
        vatRate: 20,
        vatCollectionTime: "Au décaissement",
    };

    blankStaff = {
        isLineReported: false,
        tag: null,
        isBoss: false,
        title: null,
        number: 1,
        details: [],
        years: [
            {
                title: "Salaire N",
                individualAmount: 0,
                totalAmount: 0,
                evolutionPercentage: null,
            },
            {
                title: "Salaire N+1",
                individualAmount: 0,
                totalAmount: 0,
                evolutionPercentage: 2.0,
            },
            {
                title: "Salaire N+2",
                individualAmount: 0,
                totalAmount: 0,
                evolutionPercentage: 2.0,
            },
        ],
        wagesTax: 22,
        employerTax: 38,
        globalTax: 40,
    };

    // HEADERS

    activitiesHeaders = [
        { title: "Étiquette", value: "tag" },
        { title: "Libellé", value: "title" },
        { title: "", value: "" },
        // {title: "Secteur", value: "sector"},
        { title: "Compte", value: "salesAccount" },
        { title: "Reporter", value: "activeReporting" },
        { title: "Montant N-1", value: "amountNM1" },
        { title: "% Report", value: "years[0].evolutionPercentage" },
        { title: "Montant N", value: "years[0].amount" },
        { title: "% Evol", value: "years[1].evolutionPercentage" },
        { title: "Montant N+1", value: "years[1].amount" },
        { title: "% Evol", value: "years[2].evolutionPercentage" },
        { title: "Montant N+2", value: "years[2].amount" },
        { title: "Marge", value: "marginRate" },
        { title: "Délai", value: "paymentDelay" },
        { title: "TVA", value: "vatRate" },
        { title: "Collecte", value: "vatCollectionTime" },
        { title: "Détails", value: "data-table-expand" },
        { title: "Actions", value: "action" },
    ];

    chargesHeaders = [
        { title: "Étiquette", value: "tag" },
        { title: "Libellé", value: "title" },
        { title: "", vlaue: "" },
        { title: "Reporter", value: "activeReporting" },
        { title: "Montant N-1", value: "amountNM1" },
        { title: "% Report", value: "years[0].evolutionPercentage" },
        { title: "Montant N", value: "years[0].amount" },
        { title: "% Evol", value: "years[1].evolutionPercentage" },
        { title: "Montant N+1", value: "years[1].amount" },
        { title: "% Evol", value: "years[2].evolutionPercentage" },
        { title: "Montant N+2", value: "years[2].amount" },
        // {title: "% Fixe", value: "fixedPercentage"},
        { title: "Fréquence", value: "frequency" },
        { title: "Délai", value: "paymentDelay" },
        { title: "TVA", value: "vatRate" },
        { title: "type de TVA", value: "vatCollectionTime" },
        { title: "Détails", value: "data-table-expand" },
        { title: "Actions", value: "action" },
    ];

    // ITEMS

    // GETTERS & SETTERS

    get dialog() {
        return this.value;
    }

    set dialog(event) {
        this.value = event;
    }

    get validDisplay() {
        if (this.step === 1 || this.step === 6) {
            return false;
        }
        return true;
    }

    get validProcess() {
        if (this.step === 2) {
            if (!this.selectedAccountingPeriod) {
                return false;
            }
        }
        return (this.step > 1 && this.step <= this.bffStartStep) || this.validBff;
    }

    get validTitle() {
        return this.step === 5
            ? this.budgetId
                ? "Modifier le budget"
                : "Créer le budget"
            : "Suivant";
    }

    get stepTitle() {
        if (this.step === 1) {
            return "Budget : choix de la méthode";
        } else if (this.step === 2) {
            return "Budget : initialisation";
        } else if (this.step === 3) {
            return "Budget : report";
        } else if (this.step === 4) {
            return "Budget : paramétrage";
        } else if (this.step === 5) {
            return "Budget : saisie des données";
        }
        return "Budget";
    }

    get selectedCompany() {
        return this.$store.state.selected_company;
    }

    get initializationPackage() {
        this.updateBlankLines();
        const blankObjects = {
            capitalContribution: this.blankCapitalContribution,
            currentAccountContribution: this.blankCurrentAccountContribution,
            loan: this.blankLoan,
            investmentGrant: this.blankInvestmentGrant,
            activity: this.blankActivity,
            recurringCharge: this.blankCharge,
            uniqueCharge: this.blankInvestment,
            staff: this.blankStaff,
        };
        return blankObjects;
    }

    get headersPackage() {
        const headers = {
            activities: this.activitiesHeaders,
            charges: this.chargesHeaders,
        };
        return headers;
    }

    get reinitializationText() {
        if (this.step === 2) {
            return "Réinitialiser la sélection";
        } else if (this.step === 4) {
            return "Réinitialiser les paramètres";
        } else if (this.step === 5) {
            return "Réinitialiser le formulaire";
        }
    }

    get displayReinitializationButton() {
        if (this.step === 6) return false;
        return this.step === 2 || this.step > 3;
    }

    get displayPrevious() {
        if (this.step <= 2) {
            return false;
        } else if (this.step === 4 && !this.showInitialization) {
            return false;
        }
        return true;
        // return this.step > 1
    }

    @Apollo({
        query() {
            return PARTITION_TAG_QUERY;
        },
        variables() {
            return {
                companyId: this.selectedCompany.id,
            };
        },
        skip() {
            return !this.selectedCompany?.id || this.skipTagQuery;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading && data && data.partitionTags) {
                this.loading = true;
                this.partitionTags = data.partitionTags;
                const emptyPartition = {
                    id: "",
                    name: "sans Partition",
                    tags: [],
                };
                const currentPartitionDeleted = !this.partitionTags.find(
                    (pt) => pt.id === this.selectedPartition,
                );
                if (!this.isTagManagerClosing || currentPartitionDeleted) {
                    this.selectedPartition = "";
                }
                this.partitionTags.push(emptyPartition);
                // this.shortenHeaders();
                // let found
                // for (const pt of this.partitionTags) {
                //     if (pt.tags.length) {
                //         found = true
                //         break;
                //     }
                // }
                if (this.isTagManagerClosing) {
                    // if we deleted the previously used partition in the tag manager, we have to reinitialize all the tags to null
                    // if we don't, some tags remain wrongly assigned and cannot be deleted
                    this.initializeDefaultTag();
                }
                // debugger
                this.skipBudgetQuery =
                    this.isTagManagerClosing || (!this.budgetId && !this.budgetIdToCopy);
                this.skip6And7Query = this.budgetId;
                this.loading = !this.isTagManagerClosing;
                this.isTagManagerClosing = false;
                this.skipTagQuery = true;
            } else {
                this.partitionTags = [];
                this.selectedPartition = null;
                // this.refreshHeadersOnPartitionChanged++
                this.defaultTag = null;
                this.loading = false;
            }
        },
    })
    partitionTags = [];

    @Apollo({
        query() {
            return REVENUE_ACCOUNTABLE_ACCOUNTS_QUERY;
        },
        variables() {
            return {
                companyId: this.selectedCompany.id,
                vatRate0: false,
            };
        },
        // debounce: 500,
        skip() {
            return !this.selectedCompany.id;
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loading = true;
            if (!loading) {
                if (data && data.companySalesAccounts && data.companySalesAccounts.salesAccounts) {
                    this.availableSalesAccounts = data.companySalesAccounts.salesAccounts;
                    if (this.availableSalesAccounts.length === 0) {
                        this.deleteSalesAccountColumn();
                    } else {
                        this.updateActivitiesSalesAccount();
                    }
                } else {
                    console.log("AccountableAccountVue data pb");
                }
            }
            this.loading = false;
        },
    })
    salesAccountableAccounts: Array<any> = [];

    @Apollo({
        query: BUDGET_QUERY,
        variables() {
            return {
                id: this.budgetId || this.budgetIdToCopy,
            };
        },
        skip() {
            return (!this.budgetId && !this.budgetIdToCopy) || this.skipBudgetQuery;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading && data && data.budget) {
                // if (data.budget.type === "MANUAL") {
                //     this.step = 6;
                // } else {
                // debugger
                this.newBudgetId = "";
                this.budgetName = data.budget.name;
                this.date = data.budget.date;
                if (data.budget.groupingMode) {
                    this.groupingMode = data.budget.groupingMode;
                }
                let loadedForm;
                if (data.budget.budgetFormLines) {
                    loadedForm = this.assembleForm(data.budget.budgetFormLines);
                } else {
                    loadedForm = data.budget.formText
                        ? jsonpack.unpack(data.budget.formText)
                        : data.budget.form;
                }
                this.formBff = {
                    fundings: loadedForm.fundings,
                    activities: loadedForm.activities,
                    charges: loadedForm.charges,
                    staff: loadedForm.staff,
                };
                this.formBfaa = data.budget.formNM1;
                if (data.budget.parameters) {
                    this.parameters = data.budget.parameters;
                } else {
                    // in case migration is needed
                    const study = {
                        periodsNumber: loadedForm.study.periodsNumber,
                        firstPeriodMonthsNumber: loadedForm.study.firstPeriodMonthsNumber,
                    };
                    const defaultParameters = loadedForm.defaultParameters;
                    this.parameters = this.sumObjects([study, defaultParameters]);
                }
                // this.updateBlankLines(); // can be done in budgetForm
                // this.updateActivitiesSalesAccount(); // can we do se in the budget form directly ?
                // this.buildActivitiesYears();
                // this.buildChargesYears();
                // this.buildStaffYears();
                // if (data.budget.relatedBudgetId) {
                //     this.relatedBfaaId = data.budget.relatedBudgetId;
                // }
                if (this.partitionTags.length && this.partitionTags.length > 1) {
                    if (data.budget.partitionTag) {
                        this.selectedPartition = data.budget.partitionTag.id;
                        // this.lengthenHeaders();
                    }
                }
                this.tagsMutationToObjects();
                this.initializeDefaultTag();
                if (this.budgetIdToCopy) {
                    this.budgetName += " (copie)";
                }
                this.skipBudgetQuery = true;
                this.showInitialization = true;
                this.instantiated = true;
                this.step = 5;
                this.parameterizeBff();
                // }
            }
            this.loading = false;
        },
    })
    loadedForm1: any = [];

    @Apollo({
        query: YEARLY_6_AND_7_BALANCES,
        variables() {
            return {
                companyId: this.selectedCompany.id,
                accountingPeriodId: this.selectedAccountingPeriod.id,
            };
        },
        skip() {
            return (
                this.budgetId ||
                this.budgetIdToCopy ||
                !this.selectedCompany ||
                !this.selectedAccountingPeriod ||
                !this.selectedAccountingPeriod.id ||
                this.skip6And7Query
            );
        },
        async specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                this.skip6And7Query = true;
                if (data && data.yearly6And7Balances) {
                    this.showInitialization = true;
                    this.formBfaa = this.dispatchLines(
                        this.toCorrectDataTypeYearlyTpcInAA(
                            data.yearly6And7Balances.yearlyBalances,
                        ),
                        false,
                    );
                    this.initializeToReport();
                }
            }
            this.loading = false;
        },
    })
    yearly6And7Balances: any = null;

    async saveBudgetForm() {
        // GQL mutation to save the JSON
        const result = await this.$apollo.mutate({
            mutation: CREATE_UPDATE_BUDGET_FULL,
            variables: {
                budgetId: this.budgetId || this.newBudgetId,
                name: this.budgetName,
                date: this.date,
                form: undefined,
                formText: jsonpack.pack(JSON.stringify(this.formBff)),
                formNM1: this.formBfaa || undefined, // can be packed to gain memory storage
                parameters: this.parameters,
                companyId: this.selectedCompany.id,
                partitionTagId: this.selectedPartition,
                groupingMode: this.bffGenerationMode,
            },
        });
        if (!this.budgetId) {
            this.newBudgetId = result.data.createUpdateBudgetFull.id;
        }
    }

    async initializeBff() {
        this.validLoading = true;
        const criterias = this.buildMonthlyRecoveryCriterias();
        const result = await this.$apollo.query({
            query: YEARLY_TO_MONTHLY_6_AND_7_BALANCES,
            variables: {
                companyId: this.selectedCompany.id,
                accountingPeriodId: this.selectedAccountingPeriod.id,
                selectionCriteriasList: criterias,
            },
        });
        const monthlyBalances = cloneDeep(result.data.yearlyToMonthly6And7Balances.monthlyBalances);

        const filledData = this.fillDetails(monthlyBalances);
        this.reportedLines = this.dispatchLines(filledData, true);
    }

    // METHODS

    onValidBffChanged(event) {
        this.validBff = event;
    }

    onClassicMethodChosen() {
        this.showInitialization = false;
        this.bffStartStep = 2;
        this.step = 4;
    }

    onNM1MethodChosen() {
        this.showInitialization = true;
        this.bffStartStep = 4;
        this.step = 2;
    }

    // onManualMethodChosen() {
    //     this.showInitialization = true;
    //     this.step = 6;
    // }

    previous() {
        if (!this.showInitialization) {
            // if (this.step === 4) {
            //     this.step = 1
            // } else
            if (this.step === 5) {
                this.step--;
            }
        } else {
            if (this.step > 2) {
                this.step--;
            }
        }
    }

    async valid() {
        this.validLoading = true;
        if (this.step === 2) {
            // this.getFormBfaa()
            this.step++;
            this.validLoading = false;
        } else if (this.step === 3) {
            await this.initializeBff();
            this.step++;
            this.validLoading = false;
        } else if (this.step === 4) {
            this.parameterizeBff();
            this.step++;
            this.validLoading = false;
        } else if (this.step === 5) {
            await this.createBff();
            this.validLoading = false;
            this.dialog = false;
        }
    }

    // getFormBfaa() {
    //     this.emitForm = true
    // }

    // onStoreFormBfaa(event) {
    //     this.formBfaa = event.form
    // }

    buildMonthlyRecoveryCriterias() {
        const criterias = [];
        for (const datasetName of this.datasets) {
            for (const aaLine of this.formBfaa[datasetName]) {
                if (aaLine.toReport) {
                    const criteria = { aa: aaLine.aaName, companies: [] };
                    for (const companyLine of aaLine.subLines) {
                        if (companyLine.toReport) {
                            const companyName =
                                companyLine.companyName === "Autres" ? "" : companyLine.companyName;
                            criteria.companies.push(companyName);
                        }
                    }
                    criterias.push(criteria);
                }
            }
        }
        return criterias;
    }

    fillDetails(data) {
        // transform the result of yearlyToMonthly6And7Balances
        // input has months list of date * amount with only months for which AL exist
        // method add the missing months with amount 0
        // PREREQUISITE : MONTHLIST HAS TO BE SORTED ACCORDING TO TIMELINE
        for (const line of data) {
            const newMonthsList = JSON.parse(JSON.stringify(line.months));
            const firstMonth = moment(this.selectedAccountingPeriod.start).startOf("month");
            for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
                const currentMonth = firstMonth.clone().add(monthIndex, "months").format("MM-YYYY");
                if (!line.months.find((month) => month.date === currentMonth)) {
                    newMonthsList.splice(monthIndex, 0, { date: currentMonth, amount: 0 });
                }
            }
            line.months = newMonthsList;
        }
        return data;
    }

    dispatchLines(linesList, toMerge) {
        let dispatched;
        if (!toMerge) {
            // the input is the yearlyLineList
            const chargesAAFilter = ["1", "2", "5", "6", "7", "8", "9"];
            const chargesList = [];
            const staffList = [];
            // const salesCostsList = [];
            const salesList = [];
            // iterating on lines, assigning each one to a list according to the beginning of their accountable account name
            for (const line of linesList) {
                if (!line || !line.aaName) {
                    debugger;
                    return null;
                } else if (line.aaName.slice(0, 1) === "6") {
                    if (line.aaName.slice(1, 2) === "0") {
                        // salesCostsList.push(line);
                        line.variability = "variable";
                        chargesList.push(line);
                    } else if (
                        chargesAAFilter.find((number) => number === line.aaName.slice(1, 2))
                    ) {
                        line.variability = "fixe";
                        chargesList.push(line);
                    }
                } else if (line.aaName.slice(0, 1) === "7") {
                    salesList.push(line);
                } else if (line.aaName.slice(0, 3) === "421") {
                    staffList.push(line);
                } else {
                    debugger;
                }
            }
            dispatched = {
                charges: chargesList,
                staff: staffList,
                // salesCosts: salesCostsList,
                sales: salesList,
            };
        } else if (this.bffGenerationMode === "TPC") {
            // the input is monthlyLineList to merge by company
            dispatched = {
                charges: [],
                staff: [],
                // salesCosts: [], // is not used but appears in the dataset to avoid errors
                sales: [],
            };
            for (const line of linesList) {
                if (!line || !line.aaName) {
                    debugger; // git-ignore
                    return null;
                } else {
                    this.findSubDataset(line, dispatched);
                }
            }
            this.mergeCompanyLines(dispatched);
        } else {
            // the input is monthlyLineList to merge by aa
            dispatched = {
                charges: [],
                staff: [],
                // salesCosts: [], // is not used but appears in the dataset to avoid errors
                sales: [],
            };
            for (const line of linesList) {
                if (!line || !line.aaName) {
                    debugger; // git-ignore
                    return null;
                } else {
                    this.findSubDataset(line, dispatched);
                }
            }
            this.mergeAaLines(dispatched);
        }
        return dispatched;
    }

    findSubDataset(line, dispatched) {
        // true if merged grouping by aa, allowing the column salesCosts to exist, else it is considered as a normal charge
        if (line.aaName.length === 6) {
            if (line.aaName.slice(0, 1) === "7") {
                dispatched.sales.push(line);
                return "sales";
            } else if (line.aaName.slice(0, 3) === "421") {
                dispatched.staff.push(line);
                return "staff";
            } else {
                dispatched.charges.push(line);
                return "charges";
            }
        }
        return "";
    }

    mergeCompanyLines(dispatched) {
        // iterate each sub dataset of dispatched and merge all the line of each dataset referencing the same company
        // for each final line, a track of all the accountable accounts involved in it has to remain
        for (const datasetName of this.datasets) {
            const mergedLines = [];
            for (const companyLine of dispatched[datasetName]) {
                const index = mergedLines.findIndex(
                    (line) => line.companyName === companyLine.companyName,
                );
                if (index < 0) {
                    const newTotalLine = {
                        aaName: null,
                        aaDesc: null,
                        companyName: companyLine.companyName,
                        companyId: companyLine.companyId,
                        budgetSecondaryKeys: [companyLine.aaName],
                        total: companyLine.total,
                        vatRate: companyLine.vatRate,
                        months: companyLine.months,
                    };
                    mergedLines.push(newTotalLine);
                } else {
                    const mergeRoot = mergedLines[index];
                    mergeRoot.budgetSecondaryKeys.push(companyLine.aaName);
                    mergeRoot.vatRate =
                        (mergeRoot.total * mergeRoot.vatRate +
                            companyLine.total * companyLine.vatRate) /
                        (mergeRoot.total + companyLine.total);
                    mergeRoot.total += companyLine.total;
                    for (const monthIndex in mergeRoot.months) {
                        mergeRoot.months[monthIndex].amount =
                            mergeRoot.months[monthIndex].amount +
                            companyLine.months[monthIndex].amount;
                    }
                }
            }
            dispatched[datasetName] = mergedLines;
        }
    }

    mergeAaLines(dispatched) {
        // same function as mergeCompanyLines but merging by aa instead of tpc
        for (const datasetName of this.datasets) {
            const mergedLines = [];
            for (const aaLine of dispatched[datasetName]) {
                const index = mergedLines.findIndex((line) => line.aaName === aaLine.aaName);
                if (index < 0) {
                    const newTotalLine = {
                        aaName: aaLine.aaName,
                        aaDesc: aaLine.aaDesc,
                        companyName: null,
                        companyId: null,
                        budgetSecondaryKeys: [aaLine.companyName],
                        total: aaLine.total,
                        vatRate: aaLine.vatRate,
                        months: aaLine.months,
                    };
                    mergedLines.push(newTotalLine);
                } else {
                    const mergeRoot = mergedLines[index];
                    mergeRoot.budgetSecondaryKeys.push(aaLine.companyName);
                    mergeRoot.vatRate =
                        (mergeRoot.total * mergeRoot.vatRate + aaLine.total * aaLine.vatRate) /
                        (mergeRoot.total + aaLine.total);
                    mergeRoot.total += aaLine.total;
                    for (const monthIndex in mergeRoot.months) {
                        mergeRoot.months[monthIndex].amount =
                            mergeRoot.months[monthIndex].amount + aaLine.months[monthIndex].amount;
                    }
                }
            }
            dispatched[datasetName] = mergedLines;
        }
    }

    @Emit("budget-created")
    budgetCreated(data) {
        return data;
    }

    @Emit("budget-modified")
    budgetModified(data) {
        return data;
    }

    async createBff() {
        // import function CREATE_UPDATE_BUDGET_FULL
        this.tagsMutationToIds();
        await this.saveBudgetForm();
        if (this.newBudgetId) {
            this.budgetCreated({
                date: this.date,
                id: this.newBudgetId,
                name: this.budgetName,
                type: "FORM_FULL",
            });
            this.newBudgetId = "";
        } else {
            // if (this.budgetModified)
            this.budgetModified({
                date: this.date,
                id: this.budgetId,
                name: this.budgetName,
                type: "FORM_FULL",
            });
        }
    }

    onValidationUpdate(event) {
        this.validBff = event;
    }

    sumObjects(objectsToSum) {
        const sum = {};
        for (const object of objectsToSum) {
            for (const key in object) {
                if (sum[key] !== undefined) {
                    // @ts-ignore
                    debugger; // error : two objects have the same attribute
                } else {
                    sum[key] = object[key];
                }
            }
        }
        return sum;
    }

    tagsMutationToObjects() {
        if (this.selectedPartition) {
            // assuming that partitionTags is already created, because tagsMutationToObjects is only used in budgetQuery which come after
            for (const capitalContribution of this.formBff.fundings.capitalContributions) {
                if (capitalContribution.tag === "") {
                    continue;
                }
                const tagId = capitalContribution.tag;
                const tag = this.findTagInPartitionTags(tagId);
                capitalContribution.tag = tag;
            }
            for (const currentAccountContribution of this.formBff.fundings
                .currentAccountContributions) {
                if (currentAccountContribution.tag === "") {
                    continue;
                }
                const tagId = currentAccountContribution.tag;
                const tag = this.findTagInPartitionTags(tagId);
                currentAccountContribution.tag = tag;
            }
            for (const loan of this.formBff.fundings.loans) {
                if (loan.tag === "") {
                    continue;
                }
                const tagId = loan.tag;
                const tag = this.findTagInPartitionTags(tagId);
                loan.tag = tag;
            }
            for (const investmentGrant of this.formBff.fundings.investmentGrants) {
                if (investmentGrant.tag === "") {
                    continue;
                }
                const tagId = investmentGrant.tag;
                const tag = this.findTagInPartitionTags(tagId);
                investmentGrant.tag = tag;
            }
            for (const activity of this.formBff.activities) {
                if (activity.tag === "") {
                    continue;
                }
                const tagId = activity.tag;
                const tag = this.findTagInPartitionTags(tagId);
                activity.tag = tag;
            }
            for (const recurringCharge of this.formBff.charges.recurringCharges) {
                if (recurringCharge.tag === "") {
                    continue;
                }
                const tagId = recurringCharge.tag;
                const tag = this.findTagInPartitionTags(tagId);
                recurringCharge.tag = tag;
            }
            for (const uniqueCharge of this.formBff.charges.uniqueCharges) {
                if (uniqueCharge.tag === "") {
                    continue;
                }
                const tagId = uniqueCharge.tag;
                const tag = this.findTagInPartitionTags(tagId);
                uniqueCharge.tag = tag;
            }
            for (const staff of this.formBff.staff) {
                if (staff.tag === "") {
                    continue;
                }
                const tagId = staff.tag;
                const tag = this.findTagInPartitionTags(tagId);
                staff.tag = tag;
            }
        }
    }

    findTagInPartitionTags(tagId) {
        for (const partition of this.partitionTags) {
            for (const tag of partition.tags) {
                if (tag.id === tagId) {
                    return tag;
                }
            }
        }
        return null; // in case there is an error, replace previous tag by the default tag
    }

    initializeDefaultTag() {
        // this.refreshHeadersOnPartitionChanged++
        const tag = null;
        for (const capitalContribution of this.formBff.fundings.capitalContributions) {
            if (!this.checkTag(capitalContribution)) {
                capitalContribution.tag = tag;
            }
        }
        for (const currentAccountContribution of this.formBff.fundings
            .currentAccountContributions) {
            if (!this.checkTag(currentAccountContribution)) {
                currentAccountContribution.tag = tag;
            }
        }
        for (const loan of this.formBff.fundings.loans) {
            if (!this.checkTag(loan)) {
                loan.tag = tag;
            }
        }
        for (const investmentGrant of this.formBff.fundings.investmentGrants) {
            if (!this.checkTag(investmentGrant)) {
                investmentGrant.tag = tag;
            }
        }
        for (const activity of this.formBff.activities) {
            if (!this.checkTag(activity)) {
                activity.tag = tag;
            }
        }
        for (const recurringCharge of this.formBff.charges.recurringCharges) {
            if (!this.checkTag(recurringCharge)) {
                recurringCharge.tag = tag;
            }
        }
        for (const uniqueCharge of this.formBff.charges.uniqueCharges) {
            if (!this.checkTag(uniqueCharge)) {
                uniqueCharge.tag = tag;
            }
        }
        for (const staff of this.formBff.staff) {
            if (!this.checkTag(staff)) {
                staff.tag = tag;
            }
        }
    }

    checkTag(budgetLine) {
        // tell whether the line's tag is apart of the selected partition or not
        let checked = false;
        for (const partition of this.partitionTags) {
            if (partition.id === this.selectedPartition) {
                for (const tag of partition.tags) {
                    if (this.areTagsEqual(tag, budgetLine.tag)) {
                        checked = true;
                        break;
                    }
                }
                break;
            }
        }
        return checked;
    }

    areTagsEqual(tag1, tag2) {
        // return true if the objects refers to the same tag
        //  function needed because "===" return false even if the values are similar -> probably because objects dont point towards the same memory location
        if (tag1 && tag2) {
            return tag1.name === tag2.name && tag1.id === tag2.id;
        } else {
            return false;
        }
    }

    initializeFormWithBfaa() {
        // delete lines reported before if any
        this.deletePreviouslyReportedLines();
        if (!this.budgetId) {
            const newStartDate = moment(this.selectedAccountingPeriod.end)
                .clone()
                .add(1, "months")
                .startOf("month")
                .format();
            this.date = newStartDate;
            // this.onDateChanged(newStartDate)
        }
        const chargesNames = [
            "isLineReported",
            "title",
            "detailsNM1",
            "amountNM1",
            "activeReporting",
            "details",
            "years",
            "vatRate",
            "budgetSecondaryKeys",
            "caracteristicId",
        ];
        for (const chargeLine of this.reportedLines.charges) {
            const details = this.reportDetails(chargeLine, true);
            const detailsNM1 = this.reportDetails(chargeLine, false);
            const years = this.reportYears(chargeLine);
            const vatRate = chargeLine.vatRate || this.parameters.vatPurchase;
            const title =
                this.bffGenerationMode === "TPC" ? chargeLine.companyName : chargeLine.aaName;
            const caracteristicId =
                this.bffGenerationMode === "TPC" ? chargeLine.companyId : chargeLine.aaName;
            const chargesValues = [
                true,
                title,
                detailsNM1,
                chargeLine.total,
                true,
                details,
                years,
                vatRate,
                this.buildBudgetSecondaryKeys(chargeLine.budgetSecondaryKeys),
                caracteristicId,
            ];
            const newChargeLine = this.createLine(chargesNames, chargesValues, this.blankCharge);
            this.formBff.charges.recurringCharges.push(newChargeLine);
        }
        // for (const chargeLine of this.reportedLines.salesCosts) {
        //     const details = this.reportDetails(chargeLine, true);
        //     const detailsNM1 = this.reportDetails(chargeLine, false);
        //     const years = this.reportYears(chargeLine);
        //     const vatRate = chargeLine.vatRate || this.parameters.vatPurchase;
        //     const title = this.bffGenerationMode === "TPC" ? chargeLine.companyName : chargeLine.aaName;
        //     const chargesValues = [true, title, detailsNM1, chargeLine.total, true, details, years, vatRate, this.buildBudgetSecondaryKeys(chargeLine.budgetSecondaryKeys)];
        //     const newChargeLine = this.createLine(chargesNames, chargesValues, this.blankCharge);
        //     this.formBff.charges.recurringCharges.push(newChargeLine);
        // }
        const activitiesNames = [
            "isLineReported",
            "title",
            "detailsNM1",
            "amountNM1",
            "activeReporting",
            "details",
            "years",
            "vatRate",
            "budgetSecondaryKeys",
            "caracteristicId",
        ];
        for (const activityLine of this.reportedLines.sales) {
            const details = this.reportDetails(activityLine, true);
            const detailsNM1 = this.reportDetails(activityLine, false);
            const years = this.reportYears(activityLine);
            const vatRate = activityLine.vatRate || this.parameters.vatSell;
            const title =
                this.bffGenerationMode === "TPC" ? activityLine.companyName : activityLine.aaName;
            const caracteristicId =
                this.bffGenerationMode === "TPC" ? activityLine.companyId : activityLine.aaName;
            const activitiesValues = [
                true,
                title,
                detailsNM1,
                activityLine.total,
                true,
                details,
                years,
                vatRate,
                this.buildBudgetSecondaryKeys(activityLine.budgetSecondaryKeys),
                caracteristicId,
            ];
            const newActivityLine = this.createLine(
                activitiesNames,
                activitiesValues,
                this.blankActivity,
            );
            this.formBff.activities.push(newActivityLine);
        }
        const staffNames = [
            "isLineReported",
            "title",
            "details",
            "years",
            "wagesTax",
            "employerTax",
            "globalTax",
            "budgetSecondaryKeys",
            "caracteristicId",
        ];
        for (const staffLine of this.reportedLines.staff) {
            const details = this.reportStaffDetails(staffLine, true);
            const years = this.reportYearsStaff(staffLine);
            const title =
                this.bffGenerationMode === "TPC" ? staffLine.companyName : staffLine.aaName;
            const caracteristicId =
                this.bffGenerationMode === "TPC" ? staffLine.companyId : staffLine.aaName;
            const staffValues = [
                true,
                title,
                details,
                years,
                this.parameters.wagesTax,
                this.parameters.employerTax,
                this.parameters.globalTax,
                this.buildBudgetSecondaryKeys(staffLine.budgetSecondaryKeys),
                caracteristicId,
            ];
            const newStaffLine = this.createLine(staffNames, staffValues, this.blankStaff);
            this.formBff.staff.push(newStaffLine);
        }
    }

    reportYears(line) {
        const rate = 0;
        const years = [{ title: "Montant N", amount: line.total, evolutionPercentage: rate }];
        for (let yearIndex = 1; yearIndex < this.parameters.periodsNumber; yearIndex++) {
            years.push({
                title: "Montant N+" + yearIndex,
                amount: line.total,
                evolutionPercentage: 0,
            });
        }
        return years;
    }

    reportYearsStaff(line) {
        const rate = 0;
        const ecc = 1 + 0.22; // Estimated Correction Coefficient needed to transform net amount (421) into gross amount (641), here with a wages tax rate of 0.22
        const years = [
            {
                title: "Salaire N",
                evolutionPercentage: rate,
                individualAmount: line.total * ecc,
                totalAmount: line.total * ecc,
            },
        ];
        for (let yearIndex = 1; yearIndex < this.parameters.periodsNumber; yearIndex++) {
            years.push({
                title: "Salaire N+" + yearIndex,
                evolutionPercentage: rate,
                individualAmount: line.total * ecc,
                totalAmount: line.total * ecc,
            });
        }
        return years;
    }

    createLine(names, values, object) {
        // create the object whose attributes are the elements of names and the corresponding values the elements of values
        if (names.length !== values.length) {
            // @ts-ignore
            debugger;
        }
        const newObject = {};
        if (object) {
            this.copyValues(object, newObject);
        }
        for (let index = 0; index < names.length && index < values.length; index++) {
            newObject[names[index]] = values[index];
        }
        return newObject;
    }

    copyValues(blankObject, object) {
        for (const key in object) {
            if (blankObject[key] === undefined) {
                delete object[key];
            }
        }
        for (const key in blankObject) {
            object[key] = blankObject[key];
        }
    }

    reduceYearExercise(dateList) {
        const newDateList = [];
        for (const date of dateList) {
            const mDate = moment(date, "MMMM YYYY");
            const newDate = mDate.subtract(12, "months").format("MMMM YYYY");
            newDateList.push(newDate);
        }
        return newDateList;
    }

    reportDetails(line, reportN) {
        // transform detail from bfaa format to bff format knowing periodsNumber = 1
        // the line to copy is the total of year N with index 0 in bfaaDetails
        const bfaaDetails = line.months;
        const resultDetails = [];
        const iterationsNumber = reportN ? this.parameters.periodsNumber : 1;
        const isActivity = line.aaName
            ? line.aaName.slice(0, 1) === "7"
            : line.budgetSecondaryKeys.length > 0
              ? line.budgetSecondaryKeys[0].slice(0, 1) === "7"
              : false;
        for (let yearIndex = 0; yearIndex < iterationsNumber; yearIndex++) {
            const bffDetails = [];
            const amountsLine = {
                editable: reportN,
                months: [],
                title: isActivity ? "Montant à payer" : "Prestations vendues",
                total: line.total,
                uneditedCount: 0,
            };
            const dates = reportN
                ? getOrderedMonthsPeriods(this.parameters, this.date)[yearIndex]
                : this.reduceYearExercise(getOrderedMonthsFirstPeriod(this.parameters, this.date));
            // first line to be created is amount because it's easier to compute percentagesNM1 with amountsLine already created
            // const coeff = reportN ? parseFloat((line.amountN / line.amountNM1).toFixed(2)) : 1; // allows to compute amountN without passing by the percentage
            for (let monthIndex = 0; monthIndex < bfaaDetails.length; monthIndex++) {
                amountsLine.months.push({
                    date: dates[monthIndex],
                    amount: bfaaDetails[monthIndex].amount,
                    edited: true,
                    manuallyModified: false,
                });
            }
            bffDetails.push(amountsLine);
            // compute percentages line
            const percentagesLine = {
                editable: reportN,
                months: [],
                title: "Saisonnalité",
                total: 100,
                uneditedCount: 0,
            };
            for (let monthIndex = 0; monthIndex < bfaaDetails.length; monthIndex++) {
                const percentage =
                    amountsLine.total === 0
                        ? 0
                        : parseFloat(
                              (
                                  (100 * amountsLine.months[monthIndex].amount) /
                                  amountsLine.total
                              ).toFixed(1),
                          );
                percentagesLine.months.push({
                    date: dates[monthIndex],
                    amount: percentage,
                    edited: true,
                    manuallyModified: true,
                });
            }
            bffDetails.push(percentagesLine);
            if (isActivity) {
                // if line is an activity create the margin line with a 100% marginRate
                const marginsLine = {
                    editable: reportN,
                    months: [],
                    title: "Marges sur production",
                    total: line.total,
                    uneditedCount: 0,
                };
                for (let monthIndex = 0; monthIndex < bfaaDetails.length; monthIndex++) {
                    marginsLine.months.push({
                        date: dates[monthIndex],
                        amount: bfaaDetails[monthIndex].amount,
                        edited: true,
                        manuallyModified: false,
                    });
                }
                bffDetails.push(marginsLine);
            }
            resultDetails.push(bffDetails);
        }
        return resultDetails;
    }

    deletePreviouslyReportedLines() {
        for (
            let chargeIndex = 0;
            chargeIndex < this.formBff.charges.recurringCharges.length;
            chargeIndex++
        ) {
            const charge = this.formBff.charges.recurringCharges[chargeIndex];
            if (charge.isLineReported) {
                this.formBff.charges.recurringCharges.splice(chargeIndex, 1);
                chargeIndex--;
            }
        }
        for (
            let activityIndex = 0;
            activityIndex < this.formBff.activities.length;
            activityIndex++
        ) {
            const activity = this.formBff.activities[activityIndex];
            if (activity.isLineReported) {
                this.formBff.activities.splice(activityIndex, 1);
                activityIndex--;
            }
        }
        for (let staffIndex = 0; staffIndex < this.formBff.staff; staffIndex++) {
            const staff = this.formBff.staff[staffIndex];
            if (staff.isLineReported) {
                this.formBff.staff.splice(staffIndex, 1);
                staffIndex--;
            }
        }
    }

    reportStaffDetails(line, reportN) {
        // put 1 in number, and amountN / monthCount in other cases
        const ecc = 1 + 0.22; // Estimated Correction Coefficient needed to transform net amount (421...) into gross amount (6.....), here with a wages tax rate of 0.22
        const bfaaDetails = line.months;
        const resultDetails = [];
        const iterationsNumber = reportN ? this.parameters.periodsNumber : 1;
        for (let yearIndex = 0; yearIndex < iterationsNumber; yearIndex++) {
            const bffDetails = [];
            const workForceLine = {
                editable: false,
                months: [],
                title: "Effectif",
                total: 1,
                uneditedCount: 0,
            };
            const dates = getOrderedMonthsFirstPeriod(this.parameters, this.date);
            for (let monthIndex = 0; monthIndex < bfaaDetails.length; monthIndex++) {
                workForceLine.months.push({
                    date: dates[monthIndex],
                    amount: 1,
                    edited: true,
                    manuallyModified: false,
                });
            }
            bffDetails.push(workForceLine);
            // compute percentages line
            const indivLine = {
                editable: false,
                months: [],
                title: "Salaire mensuel individuel brut",
                total: line.amountN,
                uneditedCount: 0,
            };
            for (let monthIndex = 0; monthIndex < bfaaDetails.length; monthIndex++) {
                const indivIncome = bfaaDetails[monthIndex].amount * ecc;
                indivLine.months.push({
                    date: dates[monthIndex],
                    amount: indivIncome,
                    edited: true,
                    manuallyModified: true,
                });
            }
            bffDetails.push(indivLine);
            const globalLine = {
                editable: false,
                months: [],
                title: "Salaire mensuel individuel brut",
                total: line.amountN,
                uneditedCount: 0,
            };
            for (let monthIndex = 0; monthIndex < bfaaDetails.length; monthIndex++) {
                const globalIncome = bfaaDetails[monthIndex].amount * ecc;
                globalLine.months.push({
                    date: dates[monthIndex],
                    amount: globalIncome,
                    edited: true,
                    manuallyModified: false,
                });
            }
            bffDetails.push(globalLine);
            resultDetails.push(bffDetails);
        }
        return resultDetails;
    }

    buildBudgetSecondaryKeys(keysList) {
        let stringified = "";
        for (const key of keysList) {
            stringified += key + " | ";
        }
        return stringified.slice(0, stringified.length - 3);
    }

    initializeForm() {
        if (!this.instantiated) {
            this.addCapitalContribution();
            this.addCurrentAccountContribution();
            this.addLoan();
            this.addInvestmentGrant();
            this.addActivity();
            // this.addCharge()
            this.addInvestment();
            this.addStaff();
            // this.instantiateDefaultRecurringCharges();
            if (this.reportedLines) {
                this.initializeFormWithBfaa();
            } else {
                this.instantiateDefaultRecurringCharges();
            }
            this.instantiated = true;
        }
        // else {
        //     if (this.reportedLines) {
        //         this.initializeFormWithBfaa()
        //     }
        // }
    }

    instantiateDefaultRecurringCharges() {
        this.formBff.charges.recurringCharges.push(this.createDefaultRecurringCharge("Assurances"));
        this.formBff.charges.recurringCharges.push(
            this.createDefaultRecurringCharge("Téléphone, internet"),
        );
        this.formBff.charges.recurringCharges.push(
            this.createDefaultRecurringCharge("Carburant, transports"),
        );
        this.formBff.charges.recurringCharges.push(
            this.createDefaultRecurringCharge("Eau, électricité, gaz"),
        );
        this.formBff.charges.recurringCharges.push(this.createDefaultRecurringCharge("Mutuelle"));
        this.formBff.charges.recurringCharges.push(
            this.createDefaultRecurringCharge("Nettoyage des locaux"),
        );
        this.formBff.charges.recurringCharges.push(
            this.createDefaultRecurringCharge("Budget publicité et communication"),
        );
        this.formBff.charges.recurringCharges.push(
            this.createDefaultRecurringCharge("Loyers et charges locatives"),
        );
        this.formBff.charges.recurringCharges.push(
            this.createDefaultRecurringCharge("Expert comptable, avocats"),
        );
        this.formBff.charges.recurringCharges.push(
            this.createDefaultRecurringCharge("Frais bancaires et terminal de paiement"),
        );
    }

    createDefaultRecurringCharge(title) {
        const recurringCharge = JSON.parse(JSON.stringify(this.blankCharge));
        recurringCharge.title = title;
        return recurringCharge;
    }

    reinitializeForm() {
        // code below is commented because is makes more sense to keep the current name date and partition which can be changed if needed
        // const date = moment().format();
        // this.date = date;
        // this.onDateChanged(date);
        this.formBff = {
            fundings: {
                capitalContributions: [],
                currentAccountContributions: [],
                loans: [],
                investmentGrants: [],
            },
            activities: [],
            charges: {
                recurringCharges: [],
                uniqueCharges: [],
            },
            staff: [],
        };
        // this.budgetName = "Nouveau scénario (full)";
        // this.instantiateDefaultRecurringCharges();
        this.instantiated = false;
        this.initializeForm();
    }

    addInvestment() {
        this.formBff.charges.uniqueCharges.push(JSON.parse(JSON.stringify(this.blankInvestment)));
    }

    addCapitalContribution() {
        this.formBff.fundings.capitalContributions.push(
            JSON.parse(JSON.stringify(this.blankCapitalContribution)),
        );
    }

    addCurrentAccountContribution() {
        this.formBff.fundings.currentAccountContributions.push(
            JSON.parse(JSON.stringify(this.blankCurrentAccountContribution)),
        );
    }

    addLoan() {
        this.formBff.fundings.loans.push(JSON.parse(JSON.stringify(this.blankLoan)));
    }

    addInvestmentGrant() {
        this.formBff.fundings.investmentGrants.push(
            JSON.parse(JSON.stringify(this.blankInvestmentGrant)),
        );
    }

    addActivity() {
        this.formBff.activities.push(JSON.parse(JSON.stringify(this.blankActivity)));
    }

    addCharge() {
        this.formBff.charges.recurringCharges.push(JSON.parse(JSON.stringify(this.blankCharge)));
    }

    addStaff() {
        this.formBff.staff.push(JSON.parse(JSON.stringify(this.blankStaff)));
    }

    updateBlankLines() {
        this.blankInvestment.vatRate = this.parameters.vatImmo;
        this.blankActivity.vatRate = this.parameters.vatSell;
        this.blankActivity.paymentDelay = this.parameters.paymentDelayClient;
        this.blankCharge.vatRate = this.parameters.vatPurchase;
        this.blankCharge.paymentDelay = this.parameters.paymentDelaySupplierPurchase;
        this.blankStaff.wagesTax = this.parameters.wagesTax;
        this.blankStaff.employerTax = this.parameters.employerTax;
        this.blankStaff.globalTax = this.parameters.globalTax;
    }

    buildActivitiesYears() {
        const years = [];
        const newLength = this.parameters.periodsNumber;
        const firstYear = {
            title: "Montant N",
            amount: 0,
            evolutionPercentage: null,
        };
        years.unshift(firstYear);
        for (let yearIndex = 1; yearIndex < newLength; yearIndex++) {
            const currentYear = {
                title: "Montant N+" + yearIndex,
                amount: years[yearIndex - 1].amount,
                evolutionPercentage: 0,
            };
            years.push(currentYear);
        }
        const previousLength = this.blankActivity.years.length;
        this.blankActivity.years = years;
        this.buildActivitiesHeaders(previousLength);
        for (const activity of this.formBff.activities) {
            const activityPreviousLength = activity.years.length;
            const lengthGap = newLength - activityPreviousLength;
            if (lengthGap < 0) {
                activity.years.splice(newLength, -lengthGap);
            } else {
                for (let yearIndex = activityPreviousLength; yearIndex < newLength; yearIndex++) {
                    const currentYear = {
                        title: "Montant N+" + yearIndex,
                        amount: activity.years[yearIndex - 1].amount,
                        evolutionPercentage: 0,
                    };
                    activity.years.push(currentYear);
                }
            }
            this.buildActivityDefaultDetails(activity);
        }
    }

    buildActivitiesHeaders(previousLength) {
        const newLength = this.blankActivity.years.length;
        let insertionIndex = this.findInsertionIndex();
        if (newLength === 1) {
            this.activitiesHeaders.splice(insertionIndex + 2, 2 * (previousLength - 1));
        } else {
            for (let yearIndex = 1; yearIndex < newLength; yearIndex++) {
                const columnPercentage = {
                    title: "%Evol",
                    value: "years[yearIndex].evolutionPercentage",
                };
                const columnAmount = {
                    title: "Montant N+" + yearIndex,
                    value: "years[yearIndex].amount",
                };
                insertionIndex += 2; // take partition and sales account presence into account
                const deletionsNumber = yearIndex === 1 ? 2 * (previousLength - 1) : 0;
                this.activitiesHeaders.splice(
                    insertionIndex,
                    deletionsNumber,
                    columnPercentage,
                    columnAmount,
                );
            }
        }
    }

    findInsertionIndex() {
        let insertionIndex = 4;
        if (this.activitiesHeaders.find((raw) => raw.value === "tag")) {
            insertionIndex++;
        }
        if (this.activitiesHeaders.find((raw) => raw.value === "salesAccount")) {
            insertionIndex++;
        }
        return insertionIndex;
    }

    buildActivityDefaultDetails(item) {
        const details = [];
        const previousDetails = JSON.parse(JSON.stringify(item.details));
        for (let yearIndex = 0; yearIndex < 5; yearIndex++) {
            let detailsYear = [];
            const yearlyPreviousDetails = previousDetails[yearIndex];
            const start = this.latestDate || this.date;
            detailsYear = buildActivityYearlyDetailsItems(
                item,
                yearIndex,
                yearlyPreviousDetails,
                this.parameters,
                start,
            );
            details.push(detailsYear);
        }
        item.details = details;
    }

    deleteSalesAccountColumn() {
        let columnIndex;
        for (let index = 0; index < this.activitiesHeaders.length; index++) {
            if (this.activitiesHeaders[index].value === "salesAccount") {
                columnIndex = index;
                break;
            }
        }
        this.activitiesHeaders.splice(columnIndex, 1);
    }

    buildChargesYears() {
        const years = [];
        const newLength = this.parameters.periodsNumber;
        const firstYear = {
            title: "Montant N",
            amount: 0,
            evolutionPercentage: null,
        };
        years.unshift(firstYear);
        for (let yearIndex = 1; yearIndex < newLength; yearIndex++) {
            const currentYear = {
                title: "Montant N+" + yearIndex,
                amount: years[yearIndex - 1].amount,
                evolutionPercentage: 0,
            };
            years.push(currentYear);
        }
        const previousLength = this.blankCharge.years.length;
        this.blankCharge.years = years;
        this.buildChargesHeaders(previousLength);
        for (const charge of this.formBff.charges.recurringCharges) {
            const chargePreviousLength = charge.years.length;
            const lengthGap = newLength - chargePreviousLength;
            if (lengthGap < 0) {
                charge.years.splice(newLength, -lengthGap);
            } else {
                for (let yearIndex = chargePreviousLength; yearIndex < newLength; yearIndex++) {
                    const currentYear = {
                        title: "Montant N+" + yearIndex,
                        amount: charge.years[yearIndex - 1].amount,
                        evolutionPercentage: 0,
                    };
                    charge.years.push(currentYear);
                }
                this.buildChargeDefaultDetails(charge);
            }
        }
    }

    buildChargesHeaders(previousLength) {
        const newLength = this.blankCharge.years.length;
        const startIndex = this.chargesHeaders.find((line) => line.value === "tag") ? 5 : 4; // this.shortHeaders ? 4 : 5;
        if (newLength === 1) {
            this.chargesHeaders.splice(startIndex + 2, 2 * (previousLength - 1));
        } else {
            for (let yearIndex = 1; yearIndex < this.blankCharge.years.length; yearIndex++) {
                const columnPercentage = {
                    title: "%Evol",
                    value: "years[yearIndex].evolutionPercentage",
                };
                const columnAmount = {
                    title: "Montant N+" + yearIndex,
                    value: "years[yearIndex].amount",
                };
                const insertionIndex = startIndex + 2 * yearIndex; // see format of chargesHeaders to understand
                const deletionsNumber = yearIndex === 1 ? 2 * (previousLength - 1) : 0;
                this.chargesHeaders.splice(
                    insertionIndex,
                    deletionsNumber,
                    columnPercentage,
                    columnAmount,
                );
            }
        }
    }

    buildChargeDefaultDetails(item) {
        const details = [];
        const previousDetails = JSON.parse(JSON.stringify(item.details));
        for (let yearIndex = 0; yearIndex < 5; yearIndex++) {
            let detailsYear = [];
            const yearlyPreviousDetails = previousDetails[yearIndex];
            const start = this.latestDate || this.date;
            detailsYear = buildChargeYearlyDetailsItems(
                item,
                yearIndex,
                yearlyPreviousDetails,
                this.parameters,
                start,
            );
            details.push(detailsYear);
        }
        item.details = details;
    }

    buildStaffYears() {
        const years = [];
        const newLength = this.parameters.periodsNumber;
        const firstYear = {
            title: "Salaire N",
            individualAmount: 0,
            totalAmount: 0,
            evolutionPercentage: null,
        };
        years.unshift(firstYear);
        for (let yearIndex = 1; yearIndex < newLength; yearIndex++) {
            const currentYear = {
                title: "Salaire N+" + yearIndex,
                individualAmount: years[yearIndex - 1].individualAmount * 2.0,
                totalAmount: years[yearIndex - 1].totalAmount * 2.0,
                evolutionPercentage: 2.0,
            };
            years.push(currentYear);
        }
        const previousLength = this.blankStaff.years.length;
        this.blankStaff.years = years;
        for (const staff of this.formBff.staff) {
            const staffPreviousLength = staff.years.length;
            const lengthGap = newLength - staffPreviousLength;
            if (lengthGap < 0) {
                staff.years.splice(newLength, -lengthGap);
            } else {
                for (let yearIndex = staffPreviousLength; yearIndex < newLength; yearIndex++) {
                    const evolutionPercentage = staff.years[yearIndex - 1].evolutionPercentage
                        ? staff.years[yearIndex - 1].evolutionPercentage
                        : 2.0;
                    const currentYear = {
                        title: "Salaire N+" + yearIndex,
                        individualAmount:
                            staff.years[yearIndex - 1].individualAmount * evolutionPercentage,
                        totalAmount: staff.years[yearIndex - 1].totalAmount * evolutionPercentage,
                        evolutionPercentage: evolutionPercentage,
                    };
                    staff.years.push(currentYear);
                }
            }
            this.buildStaffDefaultDetails(staff);
        }
    }

    buildStaffDefaultDetails(item) {
        const details = [];
        const previousDetails = JSON.parse(JSON.stringify(item.details));
        for (let yearIndex = 0; yearIndex < 5; yearIndex++) {
            let detailsYear = [];
            const yearlyPreviousDetails = previousDetails[yearIndex];
            const start = this.latestDate || this.date;
            detailsYear = buildStaffYearlyDetailsItems(
                item,
                yearIndex,
                yearlyPreviousDetails,
                this.parameters,
                start,
            );
            details.push(detailsYear);
        }
        item.details = details;
    }

    updateActivitiesSalesAccount() {
        if (this.availableSalesAccounts && this.availableSalesAccounts.length > 0) {
            const defaultSalesAccount = this.availableSalesAccounts[0].name;
            this.blankActivity.salesAccount = defaultSalesAccount;
            for (const activity of this.formBff.activities) {
                if (this.salesAccountInList(activity.salesAccount)) {
                    continue;
                }
                activity.salesAccount = defaultSalesAccount;
            }
        } else {
            for (const activity of this.formBff.activities) {
                activity.salesAccount = null;
            }
        }
        // this.buildActivitiesYears()
    }

    salesAccountInList(salesAccount) {
        for (const aa of this.availableSalesAccounts) {
            if (salesAccount === aa.name) {
                return true;
            }
        }
        return false;
    }

    onReinitializeForm() {
        this.reinitializeForm();
    }

    parameterizeBff() {
        this.buildActivitiesYears();
        this.buildChargesYears();
        this.buildStaffYears();
        this.initializeForm();
    }

    toCorrectDataTypeYearlyTpcInAA(yearlyLines) {
        // TRAITER LE CAS DES LIGNES DE SOCIAL OU ON A NON PAS DES TPC MAIS DES USER AVEC FIRSTNAME ET LASTNAME -> OK
        const yearly6And7Balances = [];
        for (const yearlyLine of yearlyLines) {
            const possibleIndex = yearly6And7Balances.findIndex(
                (line) => line.aaName === yearlyLine.name,
            );
            if (possibleIndex >= 0) {
                const companyName = yearlyLine.companyName
                    ? yearlyLine.companyName
                    : yearlyLine.firstname || yearlyLine.lastname
                      ? yearlyLine.lastname + " " + yearlyLine.firstname
                      : "Autres";
                const existingLine = yearly6And7Balances[possibleIndex];
                const subLine = this.createYearlySubLine(companyName, yearlyLine.amount);
                existingLine.subLines.push(subLine);
                existingLine.amountNM1 += yearlyLine.amount;
            } else {
                const companyName = yearlyLine.companyName
                    ? yearlyLine.companyName
                    : yearlyLine.firstname || yearlyLine.lastname
                      ? yearlyLine.lastname + " " + yearlyLine.firstname
                      : "Autres";
                const subLine = this.createYearlySubLine(companyName, yearlyLine.amount);
                const globalLine = this.createYearlyGlobalLine(
                    yearlyLine.name,
                    yearlyLine.description,
                    yearlyLine.amount,
                    subLine,
                );
                yearly6And7Balances.push(globalLine);
            }
        }
        return yearly6And7Balances;
    }

    createYearlyGlobalLine(aaName, aaDesc, amount, subLine) {
        const newGlobalLine = {
            toReport: true,
            aaName: aaName,
            aaDescription: aaDesc,
            amountNM1: amount,
            variability: "fixe",
            subLines: [subLine],
        };
        return newGlobalLine;
    }

    createYearlySubLine(companyName, amount) {
        const newSubLine = {
            toReport: true,
            companyName: companyName,
            amountNM1: amount,
        };
        return newSubLine;
    }

    toReportFilter(amountsLine) {
        // if the filter is passed by the amountsLine, then default value of toReport remains true, else it become false
        // filter contains criterias relative to amount, accountable account (and expense number in this line (create a query to compute that) ?)
        const aaToBan = [];
        const amountInfLimit = 1000;
        if (aaToBan.find((aa) => aa === amountsLine.accountableAccount.name.slice(0, aa.length))) {
            amountsLine.toReport = false;
        } else if (amountInfLimit > Math.abs(amountsLine.amountNM1)) {
            amountsLine.toReport = false;
        }
    }

    initializeToReport() {
        for (const datasetName of this.datasets) {
            for (const amountsLine of this.formBfaa[datasetName]) {
                this.toReportFilter(amountsLine);
            }
        }
    }

    tagsMutationToIds() {
        for (const capitalContribution of this.formBff.fundings.capitalContributions) {
            const tagObject = capitalContribution.tag;
            if (tagObject && tagObject.id) {
                capitalContribution.tag = tagObject.id;
            } else {
                capitalContribution.tag = "";
            }
        }
        for (const currentAccountContribution of this.formBff.fundings
            .currentAccountContributions) {
            const tagObject = currentAccountContribution.tag;
            if (tagObject && tagObject.id) {
                currentAccountContribution.tag = tagObject.id;
            } else {
                currentAccountContribution.tag = "";
            }
        }
        for (const loan of this.formBff.fundings.loans) {
            const tagObject = loan.tag;
            if (tagObject && tagObject.id) {
                loan.tag = tagObject.id;
            } else {
                loan.tag = "";
            }
        }
        for (const investmentGrant of this.formBff.fundings.investmentGrants) {
            const tagObject = investmentGrant.tag;
            if (tagObject && tagObject.id) {
                investmentGrant.tag = tagObject.id;
            } else {
                investmentGrant.tag = "";
            }
        }
        for (const activity of this.formBff.activities) {
            const tagObject = activity.tag;
            if (tagObject && tagObject.id) {
                activity.tag = tagObject.id;
            } else {
                activity.tag = "";
            }
        }
        for (const recurringCharge of this.formBff.charges.recurringCharges) {
            const tagObject = recurringCharge.tag;
            if (tagObject && tagObject.id) {
                recurringCharge.tag = tagObject.id;
            } else {
                recurringCharge.tag = "";
            }
        }
        for (const uniqueCharge of this.formBff.charges.uniqueCharges) {
            const tagObject = uniqueCharge.tag;
            if (tagObject && tagObject.id) {
                uniqueCharge.tag = tagObject.id;
            } else {
                uniqueCharge.tag = "";
            }
        }
        for (const staff of this.formBff.staff) {
            const tagObject = staff.tag;
            if (tagObject && tagObject.id) {
                staff.tag = tagObject.id;
            } else {
                staff.tag = "";
            }
        }
    }

    openTagManager() {
        this.displayTagManager = true;
    }

    getCreatedPartitionTags() {
        this.isTagManagerClosing = true;
        this.skipTagQuery = false;
    }

    onDateChanged(date) {
        // modifier les dates de chaque lignes du form i.e les dates des fundings et les détails des autres
        this.latestDate = date;
        this.buildActivitiesDefaultDetails();
        this.buildChargesDefaultDetails();
        this.buildStaffsDefaultDetails();
    }

    reinitialize() {
        if (this.step === 2) {
            this.reinitializeToReport();
        } else if (this.step === 4) {
            this.reinitializeParameters();
        } else if (this.step === 5) {
            this.reinitializeForm();
        }
    }

    reinitializeToReport() {
        this.resetToReport();
        this.initializeToReport();
    }

    reinitializeParameters() {
        this.parameters = {
            periodsNumber: 3,
            firstPeriodMonthsNumber: 12,
            // extendedPrevisionalDuration: 5,
            paymentDelayClient: 1,
            paymentDelaySupplierPurchase: 1,
            paymentDelaySupplierCosts: 1,
            vatSell: 20.0,
            vatPurchase: 20.0,
            vatImmo: 20.0,
            vatCost: 20.0,
            vatOther: 20.0,
            wagesTax: 22.0,
            employerTax: 38.0,
            globalTax: 40.0,
            // monthOfWagesPayment: "Mois en cours",
            // percentageOfCalendarQuarterPayment: 0,
            // percentageOfMonthlyPayment: 100,
            // paidHolidayFund: {
            //     disabled: true,
            //     employerContributionRate: 20,
            //     holidaysSaisonnality: 0
            // },
            loanInsuranceMode: "CE",
        };
    }

    resetToReport() {
        for (const datasetName of this.datasets) {
            for (const globalLine of this.formBfaa[datasetName]) {
                globalLine.toReport = true;
                for (const subLine of globalLine.subLines) {
                    subLine.toReport = true;
                }
            }
        }
    }

    assembleForm(budgetFormLines) {
        const budgetFormLinesSorted = budgetFormLines.sort((a, b) => {
            if (moment(a.createdAt).isBefore(moment(b.createdAt))) {
                return -1;
            } else if (moment(a.createdAt).isAfter(moment(b.createdAt))) {
                return 1;
            }
            return 0;
        });
        const form = {
            fundings: {
                capitalContributions: [],
                currentAccountContributions: [],
                loans: [],
                investmentGrants: [],
            },
            activities: [],
            charges: {
                recurringCharges: [],
                uniqueCharges: [],
            },
            staff: [],
        };
        for (const budgetFormLine of budgetFormLinesSorted) {
            const list = this.findListToAggregate(form, budgetFormLine.type);
            // this.updateBudgetFormLineAttributes(budgetFormLine)
            const formLine = budgetFormLine.data;
            list.push(formLine);
        }
        return form;
    }

    findListToAggregate(form, type) {
        if (type === "CAPITAL_CONTRIBUTION") {
            return form.fundings.capitalContributions;
        } else if (type === "CURRENT_ACCOUNT_CONTRIBUTION") {
            return form.fundings.currentAccountContributions;
        } else if (type === "LOAN") {
            return form.fundings.loans;
        } else if (type === "INVESTMENT_GRANT") {
            return form.fundings.investmentGrants;
        } else if (type === "ACTIVITY") {
            return form.activities;
        } else if (type === "RECURRING_CHARGE") {
            return form.charges.recurringCharges;
        } else if (type === "UNIQUE_CHARGE") {
            return form.charges.uniqueCharges;
        } else if (type === "STAFF") {
            return form.staff;
        } else {
            debugger;
        }
    }

    // updateBudgetFormLineAttributes(budgetFormLine) {
    //     using the real objects pointed in the line to update information if needed --> left for the moment because too complex for not much interest
    //     const line = budgetFormLine.data
    //     if (line.caracteristicId) {
    //         if (line.caracteristicId.length === 6) {
    //             // it is an accountable account name, whether aaName or salesAccount
    //             if (line.salesAccount) {

    //             }
    //         }
    //     }
    // }

    buildActivitiesDefaultDetails() {
        for (const activity of this.formBff.activities) {
            this.buildActivityDefaultDetails(activity);
        }
    }

    buildChargesDefaultDetails() {
        for (const charge of this.formBff.charges.recurringCharges) {
            this.buildChargeDefaultDetails(charge);
        }
    }

    buildStaffsDefaultDetails() {
        for (const staff of this.formBff.staff) {
            this.buildStaffDefaultDetails(staff);
        }
    }
}

export default toNative(FullBudgetProcess);
</script>

<style></style>
